import { parseFrameTime, tilesBase } from './timeline'

/**
 * Imagery layers with dated archives (NAIP, Sentinel-2): the years (or months) a layer can
 * show, from `https://pmtiles.basemap.com/tiles/<ServerLayerName>/frames.json`. Same body as
 * a time layer's index.json with `"mode":"imagery"`; each frame is the newest imagery per
 * quad as of that date, so every frame has full coverage. Ported from iOS
 * (ImageryYearController.swift) and Android (ImageryYearView.kt); change them together.
 *
 * The list lives in frames.json rather than index.json on purpose: a pmtiles layer with an
 * index.json would be taken for a weather layer and joined to the time bar.
 *
 *   { layer: "naip", step: "P1Y", mode: "imagery", default: "2025-12-31",
 *     times: ["2023-12-31", "2025-12-31"], min_zoom: 12, max_zoom: 17,
 *     frames: { "2023-12-31": { label: "2023", model: "NAIP", rev: "1792660010" }, ... } }
 */

const MAX_AGE_MS = 3600 * 1000   // frames.json's own Cache-Control

/** Worth probing: tiles on the pmtiles CDN with a `default` segment, and not a weather layer. */
export function mayHaveImageryYears(layer) {
  return !!(layer && !layer.timeStep && typeof layer.cloudfrontUrl === 'string' &&
    layer.cloudfrontUrl.includes('pmtiles.basemap.com/tiles/') && layer.cloudfrontUrl.includes('/default/'))
}

export function framesUrl(layer) {
  const base = tilesBase(layer && layer.cloudfrontUrl)
  return base ? `${base}frames.json` : null
}

/** {frames (oldest first), defaultKey, model, minZoom} from a frames.json body, or null. */
export function parseImageryFrames(index) {
  if (!index || index.mode !== 'imagery' || !Array.isArray(index.times)) return null
  const info = index.frames || {}
  const keys = index.times
    .filter(key => parseFrameTime(key) != null)
    .sort((a, b) => parseFrameTime(a) - parseFrameTime(b))
  if (keys.length === 0) return null
  const frames = keys.map(key => ({
    key,
    label: (info[key] && info[key].label) || key.slice(0, 4),
    rev: (info[key] && info[key].rev) || null
  }))
  const model = keys.map(k => info[k] && info[k].model).find(Boolean) || null
  return {
    frames,
    defaultKey: keys.includes(index.default) ? index.default : keys[keys.length - 1],
    model,
    minZoom: typeof index.min_zoom === 'number' ? index.min_zoom : null
  }
}

/** `.../default/{z}/{x}/{y}.png` becomes `.../2023-12-31/{z}/{x}/{y}.png?r=<rev>`. */
export function imageryTemplate(cloudfrontUrl, frame) {
  if (typeof cloudfrontUrl !== 'string' || !cloudfrontUrl.includes('/default/') || !frame) return null
  const url = cloudfrontUrl.replace('/default/', `/${frame.key}/`)
  return frame.rev ? `${url}${url.includes('?') ? '&' : '?'}r=${encodeURIComponent(frame.rev)}` : url
}

/** date.json for the imagery under a point: `<base>date.json?lon=..&lat=..&t=<key>`. */
export function dateUrl(layer, lng, lat, key) {
  const base = tilesBase(layer && layer.cloudfrontUrl)
  if (!base) return null
  return `${base}date.json?lon=${lng.toFixed(2)}&lat=${lat.toFixed(2)}&t=${encodeURIComponent(key)}`
}

const monthFormat = new Intl.DateTimeFormat(undefined, { month: 'short', year: 'numeric', timeZone: 'UTC' })

/** "NAIP · Aug 2024" from a date.json body's "2024-08-28", or null. */
export function captionFrom(model, body) {
  const m = body && typeof body.date === 'string' && /^(\d{4})-(\d{2})-(\d{2})$/.exec(body.date)
  if (!m) return null
  return `${model || 'NAIP'} · ${monthFormat.format(Date.UTC(+m[1], +m[2] - 1, +m[3]))}`
}

// ---- Fetching ---------------------------------------------------------------------------

const entries = new Map()    // url -> { frames, fetched }
const inFlight = new Map()   // url -> promise

/**
 * The layer's imagery frames, or null. Every definite answer (any HTTP status, "none"
 * included) is kept for the hour; a network failure keeps the last answer and is retried
 * next time. Any failure means "no years": the layer draws as it always has.
 */
export function fetchImageryFrames(layer, now = Date.now()) {
  const url = framesUrl(layer)
  if (!url || !mayHaveImageryYears(layer)) return Promise.resolve(null)
  const entry = entries.get(url)
  if (entry && now - entry.fetched < MAX_AGE_MS) return Promise.resolve(entry.frames)
  if (inFlight.has(url)) return inFlight.get(url)
  const promise = fetch(url, { headers: { Accept: 'application/json' } })
    .then(res => (res.ok ? res.json().catch(() => null) : Promise.resolve(null)).then(body => {
      const frames = parseImageryFrames(body)
      entries.set(url, { frames, fetched: Date.now() })
      return frames
    }))
    .catch(() => (entries.get(url) ? entries.get(url).frames : null))
    .finally(() => inFlight.delete(url))
  inFlight.set(url, promise)
  return promise
}

// ---- Picks ------------------------------------------------------------------------------

/**
 * The year each imagery layer shows, for this page session. Only non-default picks are
 * kept; nothing is persisted, so a new session starts on the latest imagery (as iOS).
 */
const picks = new Map()      // layerId -> frame

export function pickFor(layerId) {
  return picks.get(layerId) || null
}

/** Returns true when the pick changed. */
export function setPick(layerId, frame) {
  const current = picks.get(layerId) || null
  if ((current && current.key) === (frame && frame.key) && (current && current.rev) === (frame && frame.rev)) return false
  if (frame) picks.set(layerId, frame)
  else picks.delete(layerId)
  return true
}
